/**
 * QualityTierManager — 5-Tier FPS-Driven Quality State Machine
 *
 * Adapted from AstroMonix 3D Visualizer LOD system for NeuroPsyMap's 2D Canvas rendering.
 *
 * Tiers: high → medium → low → ultra → extreme
 *
 * Design Principles:
 * - Polygon/sprite reduction is the PRIMARY lever (via visualEvolution LOD levels)
 * - Canvas DPR scaling is SECONDARY (only at extreme tier)
 * - Frame skipping is TERTIARY (only at extreme tier)
 * - GOLDEN RULE: NEVER hide elements entirely — always fade to low opacity
 * - Panel-busy guard: freeze degradation during heavy DOM work
 * - Polish catchup: fire skipped visual effects once on busy→idle transition
 * - Hysteresis: require sustained good FPS for recovery, instant for degradation
 */

export type QualityTier = 'high' | 'medium' | 'low' | 'ultra' | 'extreme';

export interface QualityTierConfig {
  /** Current quality tier */
  currentTier: QualityTier;
  /** FPS below which to degrade one tier (instant) */
  degradeThresholds: Record<QualityTier, number>;
  /** FPS above which to consider recovery */
  recoverThresholds: Record<QualityTier, number>;
  /** Consecutive good frames required to recover one tier */
  recoverFrameCounts: Record<QualityTier, number>;
  /** Number of consecutive good frames so far */
  goodFramesInARow: number;
  /** Smoothed FPS value (EMA) */
  smoothedFPS: number;
  /** Previous-frame panel-busy state (for busy→idle detection) */
  wasPanelBusy: boolean;
  /** Frame skip divisor (1 = every frame, 2 = every other, etc.) */
  frameSkipRate: number;
  /** Counter for frame skip logic */
  frameSkipCounter: number;
  /** Polish catchup flag: fire skipped effects in next frame */
  polishCatchup: boolean;
  /** Whether performance panel is currently busy with DOM work */
  panelBusy: boolean;
  /** Whether camera/network has changed significatly (for render-on-demand) */
  sceneDirty: boolean;
}

export interface LodQualityState {
  /** Current quality tier */
  tier: QualityTier;
  /** Current FPS (smoothed) */
  fps: number;
  /** Whether system is currently degraded from nominal */
  isDegraded: boolean;
  /** Frame skip divisor */
  frameSkip: number;
  /** Whether to skip render this frame */
  shouldSkipFrame: boolean;
  /** Whether to do a polish catchup this frame */
  doCatchup: boolean;
  /** Scene-element LOD scalars for this tier */
  scalars: LodScalars;
  /** Per-subsystem update intervals in frames */
  updateIntervals: UpdateIntervals;
}

export interface LodScalars {
  /** Grid opacity multiplier */
  gridOpacity: number;
  /** Grid line skip (show every Nth line) */
  gridLineSkip: number;
  /** Connection/edge opacity multiplier */
  edgeOpacity: number;
  /** Maximum connection render distance */
  maxConnectionDistance: number;
  /** Node glow intensity multiplier */
  glowIntensityMultiplier: number;
  /** Particle emission multiplier */
  particleMultiplier: number;
  /** Whether to render background atmosphere */
  renderAtmosphere: boolean;
  /** Whether to render energy fields */
  renderEnergyFields: boolean;
  /** Whether to render lightning effects */
  renderLightning: boolean;
  /** Whether to render limb systems */
  renderLimbs: boolean;
  /** Canvas DPR (device pixel ratio) target */
  targetDPR: number;
}

export interface UpdateIntervals {
  /** LOD quality check (frames) */
  lod: number;
  /** Label/spatial update (frames) */
  labels: number;
  /** Rotation physics update (frames) */
  rotation: number;
  /** General physics update (frames) */
  physics: number;
  /** Stats/info update (frames) */
  stats: number;
}

// ── Default Tier Parameters ──

const DEFAULT_DEGRADE_THRESHOLDS: Record<QualityTier, number> = {
  high:    30,   // Drop to medium at <30 FPS
  medium:  22,   // Drop to low at <22 FPS
  low:     16,   // Drop to ultra at <16 FPS
  ultra:   10,   // Drop to extreme at <10 FPS
  extreme: 0     // Can't go lower
};

const DEFAULT_RECOVER_THRESHOLDS: Record<QualityTier, number> = {
  extreme: 16,   // >16 FPS sustained → ultra
  ultra:   24,   // >24 FPS sustained → low
  low:     35,   // >35 FPS sustained → medium
  medium:  45,   // >45 FPS sustained → high
  high:    Infinity
};

const DEFAULT_RECOVER_FRAME_COUNTS: Record<QualityTier, number> = {
  extreme: 20,   // 20 consecutive good frames
  ultra:   30,
  low:     50,
  medium:  80,   // 80 consecutive good frames to go back to high
  high:    Infinity
};

// ── Tier-Specific Scalars ──

const TIER_SCALARS: Record<QualityTier, LodScalars> = {
  high: {
    gridOpacity: 0.40, gridLineSkip: 0, edgeOpacity: 1.0,
    maxConnectionDistance: Infinity, glowIntensityMultiplier: 1.0,
    particleMultiplier: 1.0, renderAtmosphere: true,
    renderEnergyFields: true, renderLightning: true,
    renderLimbs: true, targetDPR: -1  // -1 = use device DPR
  },
  medium: {
    gridOpacity: 0.30, gridLineSkip: 0, edgeOpacity: 1.0,
    maxConnectionDistance: Infinity, glowIntensityMultiplier: 1.0,
    particleMultiplier: 0.75, renderAtmosphere: true,
    renderEnergyFields: true, renderLightning: true,
    renderLimbs: true, targetDPR: -1
  },
  low: {
    gridOpacity: 0.15, gridLineSkip: 1, edgeOpacity: 0.7,
    maxConnectionDistance: 800, glowIntensityMultiplier: 0.6,
    particleMultiplier: 0.4, renderAtmosphere: true,
    renderEnergyFields: true, renderLightning: true,
    renderLimbs: false, targetDPR: -1
  },
  ultra: {
    gridOpacity: 0.08, gridLineSkip: 2, edgeOpacity: 0.4,
    maxConnectionDistance: 500, glowIntensityMultiplier: 0.3,
    particleMultiplier: 0.15, renderAtmosphere: false,
    renderEnergyFields: false, renderLightning: false,
    renderLimbs: false, targetDPR: -1
  },
  extreme: {
    gridOpacity: 0.03, gridLineSkip: 4, edgeOpacity: 0.2,
    maxConnectionDistance: 250, glowIntensityMultiplier: 0.12,
    particleMultiplier: 0.05, renderAtmosphere: false,
    renderEnergyFields: false, renderLightning: false,
    renderLimbs: false, targetDPR: 0.75
  }
};

// ── Tier-Specific Update Intervals (in frames) ──

const TIER_UPDATE_INTERVALS: Record<QualityTier, UpdateIntervals> = {
  high:    { lod: 5,  labels: 10,  rotation: 1,  physics: 2,  stats: 10 },
  medium:  { lod: 10, labels: 15,  rotation: 2,  physics: 4,  stats: 20 },
  low:     { lod: 20, labels: 30,  rotation: 4,  physics: 8,  stats: 40 },
  ultra:   { lod: 40, labels: 60,  rotation: 8,  physics: 12, stats: 80 },
  extreme: { lod: 80, labels: 120, rotation: 16, physics: 24, stats: 120 }
};

/** Frame skip rates: only extreme tier skips frames */
const FRAME_SKIP_RATES: Record<QualityTier, number> = {
  high: 1, medium: 1, low: 1, ultra: 1, extreme: 2
};

/** Mapping from QualityTier to visualEvolution LOD level (0-4) */
export const TIER_TO_VISUAL_LOD: Record<QualityTier, number> = {
  high: 0, medium: 1, low: 2, ultra: 3, extreme: 4
};

const ALL_TIERS: QualityTier[] = ['high', 'medium', 'low', 'ultra', 'extreme'];
const TIER_ORDER: Record<QualityTier, number> = {
  extreme: 0, ultra: 1, low: 2, medium: 3, high: 4
};

// ── FPS Smoothing ──

const FPS_EMA_ALPHA = 0.1; // Smoothing factor for EMA

export class QualityTierManager {
  private _config: QualityTierConfig;
  private _initialTier: QualityTier;
  private _onTierChange: Array<(tier: QualityTier, prev: QualityTier) => void> = [];

  /** Create with optional starting tier (defaults to 'low' — efficiency-first) */
  constructor(startTier: QualityTier = 'low') {
    this._initialTier = startTier;
    this._config = {
      currentTier: startTier,
      degradeThresholds: { ...DEFAULT_DEGRADE_THRESHOLDS },
      recoverThresholds: { ...DEFAULT_RECOVER_THRESHOLDS },
      recoverFrameCounts: { ...DEFAULT_RECOVER_FRAME_COUNTS },
      goodFramesInARow: 0,
      smoothedFPS: 60,
      wasPanelBusy: false,
      frameSkipRate: 1,
      frameSkipCounter: 0,
      polishCatchup: false,
      panelBusy: false,
      sceneDirty: true
    };
  }

  // ── Public API ──

  /** Call every frame with current (unsmoothed) FPS. Returns updated state. */
  update(rawFPS: number): LodQualityState {
    const cfg = this._config;

    // Smooth FPS with EMA
    cfg.smoothedFPS = cfg.smoothedFPS * (1 - FPS_EMA_ALPHA) + rawFPS * FPS_EMA_ALPHA;
    const fps = cfg.smoothedFPS;

    // Detect panel busy→idle transition
    const wasBusy = cfg.wasPanelBusy;
    cfg.wasPanelBusy = cfg.panelBusy;

    // ── Panel-busy guard: freeze degradation during DOM work ──
    // Recovery is still allowed (previous degrade can heal)
    if (!cfg.panelBusy) {
      const degradeThreshold = cfg.degradeThresholds[cfg.currentTier];
      if (fps < degradeThreshold) {
        this._degradeOneTier();
      }
    }

    // Recovery: sustained good FPS
    const recoverThreshold = cfg.recoverThresholds[cfg.currentTier];
    if (fps > recoverThreshold) {
      cfg.goodFramesInARow++;
      const needed = cfg.recoverFrameCounts[cfg.currentTier];
      if (cfg.goodFramesInARow >= needed) {
        this._recoverOneTier();
        cfg.goodFramesInARow = 0;
      }
    } else {
      // Reset counter if we drop below recovery threshold
      if (fps <= recoverThreshold) {
        cfg.goodFramesInARow = Math.max(0, cfg.goodFramesInARow - 3);
      }
    }

    // ── Fast-recovery boost on busy→idle transition ──
    if (wasBusy && !cfg.panelBusy && cfg.currentTier !== 'high') {
      this._recoverOneTier();
      cfg.polishCatchup = true;
      cfg.goodFramesInARow = 0;
    }

    // Frame skip logic
    cfg.frameSkipRate = FRAME_SKIP_RATES[cfg.currentTier];
    cfg.frameSkipCounter++;
    const shouldSkip = cfg.frameSkipRate > 1 &&
      (cfg.frameSkipCounter % cfg.frameSkipRate) !== 0;

    // Render-on-demand: skip frames when scene is static and panel busy
    const renderOnDemandSkip = cfg.panelBusy && !cfg.sceneDirty;

    // Capture and clear catchup flag
    const doCatchup = cfg.polishCatchup;
    cfg.polishCatchup = false;

    const scalars = TIER_SCALARS[cfg.currentTier];
    const intervals = TIER_UPDATE_INTERVALS[cfg.currentTier];

    return {
      tier: cfg.currentTier,
      fps: Math.round(fps),
      isDegraded: cfg.currentTier !== 'high',
      frameSkip: cfg.frameSkipRate,
      shouldSkipFrame: shouldSkip || renderOnDemandSkip,
      doCatchup,
      scalars,
      updateIntervals: intervals
    };
  }

  /** Set panel busy state (e.g., during heavy DOM operations) */
  setPanelBusy(busy: boolean): void {
    this._config.panelBusy = busy;
  }

  /** Mark scene as dirty (camera moved, network changed) */
  markSceneDirty(): void { this._config.sceneDirty = true; }
  markSceneClean(): void { this._config.sceneDirty = false; }

  /** Get current quality tier */
  get currentTier(): QualityTier { return this._config.currentTier; }

  /** Get smoothed FPS */
  get fps(): number { return this._config.smoothedFPS; }

  /** Get scalars for current tier */
  get scalars(): LodScalars { return TIER_SCALARS[this._config.currentTier]; }

  /** Get update intervals for current tier */
  get updateIntervals(): UpdateIntervals { return TIER_UPDATE_INTERVALS[this._config.currentTier]; }

  /** Force a specific quality tier */
  forceTier(tier: QualityTier): void {
    const prev = this._config.currentTier;
    this._config.currentTier = tier;
    this._config.goodFramesInARow = 0;
    this._notifyTierChange(tier, prev);
  }

  /** Reset to initial tier */
  reset(): void {
    this._config.currentTier = this._initialTier;
    this._config.goodFramesInARow = 0;
    this._config.smoothedFPS = 60;
    this._config.frameSkipCounter = 0;
    this._config.polishCatchup = false;
    this._config.sceneDirty = true;
  }

  /** Subscribe to tier changes */
  onTierChange(fn: (tier: QualityTier, prev: QualityTier) => void): () => void {
    this._onTierChange.push(fn);
    return () => {
      const idx = this._onTierChange.indexOf(fn);
      if (idx >= 0) this._onTierChange.splice(idx, 1);
    };
  }

  /** Get human-readable status */
  getStatus(): string {
    const cfg = this._config;
    return `Q=${cfg.currentTier} FPS=${cfg.smoothedFPS.toFixed(0)} ` +
      `skip=${cfg.frameSkipRate}g:${cfg.goodFramesInARow} ` +
      `busy=${cfg.panelBusy} catchup=${cfg.polishCatchup}`;
  }

  // ── Private ──

  private _degradeOneTier(): void {
    const currentIdx = TIER_ORDER[this._config.currentTier];
    if (currentIdx <= 0) return; // Already at extreme
    const nextIdx = currentIdx - 1;
    const nextTier = ALL_TIERS.find(t => TIER_ORDER[t] === nextIdx)!;
    const prev = this._config.currentTier;
    this._config.currentTier = nextTier;
    this._config.goodFramesInARow = 0;
    this._notifyTierChange(nextTier, prev);
  }

  private _recoverOneTier(): void {
    const currentIdx = TIER_ORDER[this._config.currentTier];
    if (currentIdx >= ALL_TIERS.length - 1) return; // Already at high
    const nextIdx = currentIdx + 1;
    const nextTier = ALL_TIERS.find(t => TIER_ORDER[t] === nextIdx)!;
    const prev = this._config.currentTier;
    this._config.currentTier = nextTier;
    this._config.goodFramesInARow = 0;
    this._notifyTierChange(nextTier, prev);
  }

  private _notifyTierChange(tier: QualityTier, prev: QualityTier): void {
    for (const fn of this._onTierChange) {
      try { fn(tier, prev); } catch { /* swallow */ }
    }
  }
}

// Singleton
let _qInst: QualityTierManager | null = null;
export function getQualityTierManager(startTier?: QualityTier): QualityTierManager {
  if (!_qInst) _qInst = new QualityTierManager(startTier);
  return _qInst;
}
